import { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Users,
  MoreVertical,
  Shield,
  ShieldCheck,
  UserCog,
  Mail,
  Phone,
  Calendar,
  Send,
  Clock,
  Ban,
  CheckCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  useGetAllAdminsQuery,
  useCreateAdminMutation,
  useUpdateAdminMutation,
  useDeleteAdminMutation,
  useResendInvitationMutation,
  useSuspendAdminMutation,
  useUnsuspendAdminMutation,
  useGetProfileQuery,
} from "@/store";
import type { Admin, CreateAdminData, UpdateAdminData } from "@/store/adminApi";
import { TablePagination } from "@/components/admin";
import { toast } from "sonner";

const DEFAULT_PAGE_SIZE = 20;

export function AdminUsersScreen() {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adminToEdit, setAdminToEdit] = useState<Admin | null>(null);
  const [adminToDelete, setAdminToDelete] = useState<Admin | null>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Form state for creating admin
  const [createFormData, setCreateFormData] = useState<CreateAdminData>({
    email: "",
    name: "",
    phone: "",
    role: "ADMIN",
  });

  // Form state for editing admin
  const [editFormData, setEditFormData] = useState<UpdateAdminData>({
    name: "",
    phone: "",
    role: "ADMIN",
  });

  const { data: adminsData, isLoading } = useGetAllAdminsQuery({ page, limit });
  const { data: profileData } = useGetProfileQuery();
  const [createAdmin, { isLoading: isCreating }] = useCreateAdminMutation();
  const [updateAdmin, { isLoading: isUpdating }] = useUpdateAdminMutation();
  const [deleteAdmin, { isLoading: isDeleting }] = useDeleteAdminMutation();
  const [resendInvitation, { isLoading: isResending }] = useResendInvitationMutation();
  const [suspendAdmin, { isLoading: isSuspending }] = useSuspendAdminMutation();
  const [unsuspendAdmin, { isLoading: isUnsuspending }] = useUnsuspendAdminMutation();

  const currentUser = profileData?.data;
  const admins = adminsData?.data || [];
  const pagination = adminsData?.pagination || {
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
    total: 0,
    totalPages: 0,
  };

  // Client-side filter for search (backend doesn't support search for admins)
  const filteredAdmins = admins.filter(
    (admin) =>
      !debouncedSearch ||
      admin.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      admin.email.toLowerCase().includes(debouncedSearch.toLowerCase())
  );

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleCreateAdmin = async () => {
    if (!createFormData.email || !createFormData.name) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      await createAdmin(createFormData).unwrap();
      toast.success("Admin created successfully. An invitation email has been sent.");
      setCreateDialogOpen(false);
      setCreateFormData({
        email: "",
        name: "",
        phone: "",
        role: "ADMIN",
      });
    } catch (error: any) {
      const errorMessage =
        error?.data?.error || error?.data?.message || "Failed to create admin";
      toast.error(errorMessage);
    }
  };

  const handleResendInvitation = async (admin: Admin) => {
    try {
      await resendInvitation(admin.id).unwrap();
      toast.success(`Invitation email resent to ${admin.email}`);
    } catch (error: any) {
      const errorMessage =
        error?.data?.error || error?.data?.message || "Failed to resend invitation";
      toast.error(errorMessage);
    }
  };

  const handleSuspendAdmin = async (admin: Admin) => {
    try {
      await suspendAdmin(admin.id).unwrap();
      toast.success(`${admin.name} has been suspended`);
    } catch (error: any) {
      const errorMessage =
        error?.data?.error || error?.data?.message || "Failed to suspend admin";
      toast.error(errorMessage);
    }
  };

  const handleUnsuspendAdmin = async (admin: Admin) => {
    try {
      await unsuspendAdmin(admin.id).unwrap();
      toast.success(`${admin.name} has been unsuspended`);
    } catch (error: any) {
      const errorMessage =
        error?.data?.error || error?.data?.message || "Failed to unsuspend admin";
      toast.error(errorMessage);
    }
  };

  const handleEditAdmin = async () => {
    if (!adminToEdit) return;

    // Only send fields that have values
    const updateData: UpdateAdminData = {};
    if (editFormData.name) updateData.name = editFormData.name;
    if (editFormData.phone !== undefined) updateData.phone = editFormData.phone;
    if (editFormData.role) updateData.role = editFormData.role;

    try {
      await updateAdmin({ id: adminToEdit.id, data: updateData }).unwrap();
      toast.success("Admin updated successfully");
      setEditDialogOpen(false);
      setAdminToEdit(null);
      setEditFormData({
        name: "",
        phone: "",
        role: "ADMIN",
      });
    } catch (error: any) {
      const errorMessage =
        error?.data?.error || error?.data?.message || "Failed to update admin";
      toast.error(errorMessage);
    }
  };

  const handleDeleteAdmin = async () => {
    if (!adminToDelete) return;

    try {
      await deleteAdmin(adminToDelete.id).unwrap();
      toast.success("Admin deleted successfully");
      setDeleteDialogOpen(false);
      setAdminToDelete(null);
    } catch (error: any) {
      const errorMessage =
        error?.data?.error || error?.data?.message || "Failed to delete admin";
      toast.error(errorMessage);
    }
  };

  const openEditDialog = (admin: Admin) => {
    setAdminToEdit(admin);
    setEditFormData({
      name: admin.name,
      phone: admin.phone || "",
      role: admin.role,
    });
    setEditDialogOpen(true);
  };

  // Check if admin has pending invitation (no password set yet)
  // Allow resend for any admin that is not the current user
  const canResendInvitation = (admin: Admin) => {
    return currentUser?.id !== admin.id;
  };

  // Check if the current user can delete/suspend an admin
  // Super admins can delete/suspend other super admins, but not themselves
  const canModifyAdmin = (admin: Admin) => {
    return currentUser?.id !== admin.id;
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case "SUPER_ADMIN":
        return (
          <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0">
            <ShieldCheck className="h-3 w-3 mr-1" />
            Super Admin
          </Badge>
        );
      case "ADMIN":
        return (
          <Badge variant="default" className="bg-primary">
            <Shield className="h-3 w-3 mr-1" />
            Admin
          </Badge>
        );
      case "STAFF":
        return (
          <Badge variant="secondary">
            <UserCog className="h-3 w-3 mr-1" />
            Staff
          </Badge>
        );
      default:
        return <Badge variant="outline">{role}</Badge>;
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-NG", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Admin Users</h1>
          <p className="text-muted-foreground">
            Manage administrator accounts and permissions
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Add Admin
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle>Search Admins</CardTitle>
          <CardDescription>Find admins by name or email</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search admins..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Admins Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Admins</CardTitle>
          <CardDescription>
            {pagination.total}{" "}
            {pagination.total === 1 ? "admin" : "admins"} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading admins...
            </div>
          ) : filteredAdmins.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">No admins found</p>
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Admin
              </Button>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Admin</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAdmins.map((admin) => (
                      <TableRow key={admin.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10">
                              <AvatarFallback className="bg-gradient-to-br from-primary to-primary/60 text-primary-foreground font-semibold">
                                {getInitials(admin.name)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-semibold">{admin.name}</p>
                              {currentUser?.id === admin.id && (
                                <span className="text-xs text-muted-foreground">
                                  (You)
                                </span>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 text-sm">
                              <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                              <span>{admin.email}</span>
                            </div>
                            {admin.phone && (
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Phone className="h-3.5 w-3.5" />
                                <span>{admin.phone}</span>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{getRoleBadge(admin.role)}</TableCell>
                        <TableCell>
                          {admin.isSuspended ? (
                            <Badge variant="destructive" className="gap-1">
                              <Ban className="h-3 w-3" />
                              Suspended
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="gap-1 text-green-600 border-green-600">
                              <CheckCircle className="h-3 w-3" />
                              Active
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Calendar className="h-3.5 w-3.5" />
                            <span>{formatDate(admin.createdAt)}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {/* Don't show actions for self */}
                          {currentUser?.id !== admin.id ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEditDialog(admin)}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                {canResendInvitation(admin) && (
                                  <DropdownMenuItem 
                                    onClick={() => handleResendInvitation(admin)}
                                    disabled={isResending}
                                  >
                                    <Send className="h-4 w-4 mr-2" />
                                    Resend Invitation
                                  </DropdownMenuItem>
                                )}
                                {canModifyAdmin(admin) && (
                                  <>
                                    {admin.isSuspended ? (
                                      <DropdownMenuItem
                                        onClick={() => handleUnsuspendAdmin(admin)}
                                        disabled={isUnsuspending}
                                        className="text-green-600"
                                      >
                                        <CheckCircle className="h-4 w-4 mr-2" />
                                        Unsuspend
                                      </DropdownMenuItem>
                                    ) : (
                                      <DropdownMenuItem
                                        onClick={() => handleSuspendAdmin(admin)}
                                        disabled={isSuspending}
                                        className="text-amber-600"
                                      >
                                        <Ban className="h-4 w-4 mr-2" />
                                        Suspend
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setAdminToDelete(admin);
                                        setDeleteDialogOpen(true);
                                      }}
                                      className="text-destructive"
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <TablePagination
                pagination={pagination}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Admin Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Create New Admin</DialogTitle>
            <DialogDescription>
              Add a new administrator account. They will receive an email invitation to set up their password.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                placeholder="John Doe"
                value={createFormData.name}
                onChange={(e) =>
                  setCreateFormData({ ...createFormData, name: e.target.value })
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="john@example.com"
                value={createFormData.email}
                onChange={(e) =>
                  setCreateFormData({ ...createFormData, email: e.target.value })
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="phone">Phone (Optional)</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+234 800 000 0000"
                value={createFormData.phone}
                onChange={(e) =>
                  setCreateFormData({ ...createFormData, phone: e.target.value })
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="role">Role *</Label>
              <Select
                value={createFormData.role}
                onValueChange={(value: "ADMIN" | "STAFF" | "SUPER_ADMIN") =>
                  setCreateFormData({ ...createFormData, role: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SUPER_ADMIN">Super Admin</SelectItem>
                  <SelectItem value="ADMIN">Admin</SelectItem>
                  <SelectItem value="STAFF">Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 p-3 bg-muted rounded-md text-sm text-muted-foreground">
              <Clock className="h-4 w-4 flex-shrink-0" />
              <span>An invitation email will be sent with instructions to set up a password. The link expires in 24 hours.</span>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateAdmin} disabled={isCreating}>
              {isCreating ? "Creating..." : "Create & Send Invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Admin Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Edit Admin</DialogTitle>
            <DialogDescription>
              Update admin details and permissions.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-name">Full Name</Label>
              <Input
                id="edit-name"
                placeholder="John Doe"
                value={editFormData.name}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, name: e.target.value })
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-phone">Phone</Label>
              <Input
                id="edit-phone"
                type="tel"
                placeholder="+234 800 000 0000"
                value={editFormData.phone}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, phone: e.target.value })
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-role">Role</Label>
              <Select
                value={editFormData.role}
                onValueChange={(value: "ADMIN" | "STAFF" | "SUPER_ADMIN") =>
                  setEditFormData({ ...editFormData, role: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SUPER_ADMIN">Super Admin</SelectItem>
                  <SelectItem value="ADMIN">Admin</SelectItem>
                  <SelectItem value="STAFF">Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditDialogOpen(false);
                setAdminToEdit(null);
              }}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button onClick={handleEditAdmin} disabled={isUpdating}>
              {isUpdating ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the admin account "{adminToDelete?.name}"
              ({adminToDelete?.email}). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setAdminToDelete(null);
                setDeleteDialogOpen(false);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAdmin}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
